// Lista de reglas sobre el panel común: búsqueda y estado, y la tabla en el orden común seguida de
// palancas y lo liquidado.
import type { RetailCommission } from '@fiado/type-kit';
import { userMessageOf } from '@/api/errors';
import { mxDayOffset } from '@/lib/dateHelpers';
import { formatShortMXN } from '@/shared/lib/format/money';
import type { DataTableColumn } from '@/shared/patterns/DataTable';
import {
    isDimmedLifecycleStatus,
    LifecycleListPanel,
    LifecycleNameCell,
    LifecyclePendingMark,
    LifecycleStatusBadge,
} from '@/shared/patterns/Lifecycle';
import {
    describeCondition,
    describeLevers,
    describeRuleCountdown,
    describeRuleValidity,
    publishStateOf,
    STATUS_FILTER_ORDER,
    STATUS_LIFECYCLE,
    type StatusFilter,
} from '../../lib/ruleText';

type Rule = RetailCommission.CommissionRule;

const PENDING_CHANGES = 'Cambios sin publicar';

function leversText(rule: Rule): string {
    return describeLevers(rule.levers, rule.countBy, rule.payTiming, rule.onSaleAdvanceBps);
}

interface RulesListPanelProps {
    rules: Rule[];
    isLoading: boolean;
    error: Error | null;
    onRetry: () => void;
    /** Lo liquidado por regla en el corte en curso; `null` si no se puede leer. */
    paid: Map<string, number> | null;
    search: string;
    onSearch: (value: string) => void;
    status: StatusFilter;
    onStatus: (value: StatusFilter) => void;
    selectedId: string | null;
    onSelect: (ruleId: string) => void;
    onNew: (() => void) | null;
}

export function RulesListPanel(props: RulesListPanelProps) {
    const {
        rules,
        isLoading,
        error,
        onRetry,
        paid,
        search,
        onSearch,
        status,
        onStatus,
        selectedId,
        onSelect,
        onNew,
    } = props;
    const today = mxDayOffset(0);
    const columns: DataTableColumn<Rule>[] = [
        {
            key: 'rule',
            label: 'Regla',
            cell: (rule) => (
                <LifecycleNameCell
                    name={rule.name}
                    detail={describeCondition(rule.condition, rule.countBy)}
                    dimmed={isDimmedLifecycleStatus(STATUS_LIFECYCLE[rule.status])}
                />
            ),
        },
        {
            key: 'status',
            label: 'Estado',
            width: '84px',
            cellOverflow: 'nowrap',
            cellTitle: (rule) =>
                publishStateOf(rule).kind === 'pending' ? PENDING_CHANGES : undefined,
            cell: (rule) => (
                <span className="inline-flex items-center gap-1">
                    <LifecycleStatusBadge status={STATUS_LIFECYCLE[rule.status]} />
                    {publishStateOf(rule).kind === 'pending' ? (
                        <LifecyclePendingMark label={PENDING_CHANGES} />
                    ) : null}
                </span>
            ),
        },
        {
            key: 'validity',
            label: 'Vigencia',
            width: '108px',
            cellTitle: (rule) => describeRuleValidity(rule),
            cell: (rule) => (
                <span className="text-[10.5px] leading-[1.35]">
                    {describeRuleCountdown(rule, today)}
                </span>
            ),
        },
        {
            key: 'levers',
            label: 'Palancas',
            width: '140px',
            cellTitle: leversText,
            cell: (rule) => (
                <span className="text-[10.5px] leading-[1.35]">{leversText(rule)}</span>
            ),
        },
        {
            key: 'paid',
            label: 'Liquidado',
            width: '72px',
            numeric: true,
            note: 'Corte en curso',
            cell: (rule) => {
                const cents = paid?.get(rule.ruleId) ?? 0;
                return cents > 0 ? formatShortMXN(cents / 100) : '—';
            },
        },
    ];

    return (
        <LifecycleListPanel
            search={{ label: 'Buscar regla', value: search, onChange: onSearch }}
            status={{
                value: status,
                anyValue: 'ALL',
                options: STATUS_FILTER_ORDER.map((value) => ({
                    value,
                    status: STATUS_LIFECYCLE[value],
                })),
                onChange: (value) => {
                    onStatus(value as StatusFilter);
                },
            }}
            count={{ value: rules.length, one: 'regla', many: 'reglas' }}
            create={onNew ? { label: 'Crear regla', onClick: onNew } : null}
            error={
                error
                    ? {
                          title: 'No se pudieron cargar las reglas',
                          body: userMessageOf(error),
                          onRetry,
                      }
                    : null
            }
            table={{
                columns,
                rows: rules,
                rowKey: (rule) => rule.ruleId,
                caption: 'Reglas de comisión',
                isRowSelected: (rule) => rule.ruleId === selectedId,
                onRowClick: (rule) => {
                    onSelect(rule.ruleId);
                },
                isLoading,
                empty: 'Sin reglas que cumplan el filtro.',
            }}
        />
    );
}
